import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Center,
  Code,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";

import { format } from "date-fns";
import { FiCheckCircle, FiSend } from "react-icons/fi";
import { LuMoveRight } from "react-icons/lu";
import { PiChatsCircle } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";

import { ReadQuerySchema } from "@/jenesys-types/data/communication";
import { DataType } from "@/jenesys-types/data/enums";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

import { CommentInput } from "@/components/common/CommentInput";
import { ParsedCommentRenderer } from "@/components/common/ParsedCommentRenderer";
import { PreviewFile } from "@/components/features/transactions/TranactionPreviewFile";
import useEntityRetrieve from "@/hooks/crud/useEntityRetrieve";
import {
  usePostQueryComment,
  useQueryDetails
} from "@/hooks/features/useCommunicationQueries";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import { CrudRouteType, getCrudRouteName } from "@/routes/crud";
import { selectCurrentUser } from "@/store/app/bootstrap/bootstrap";
import {
  parseComment,
  selectParsedComments
} from "@/store/features/commentParsing/commentParsing";
import {
  loadCompanyMembers,
  selectCompanyMembers
} from "@/store/features/companyMembers/companyMembers";

interface QueryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryId: string;
  companyId: string;
}

const getQueryTitle = (query: ReadQuerySchema) => {
  switch (query.type) {
    case "TRANSACTION":
      return "Transaction Query";
    case "CHASER":
      return "Chaser";
    case "GENERAL":
      return "General Query";
    default:
      return "Query";
  }
};

export const QueryDetailsModal: React.FC<QueryDetailsModalProps> = ({
  isOpen,
  onClose,
  queryId,
  companyId
}) => {
  const { query, isLoading, error } = useQueryDetails(queryId, companyId);
  const {
    postComment,
    isPosting,
    error: postError
  } = usePostQueryComment(queryId, query?.company_id);
  const [reply, setReply] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const toast = useToast();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const companyMembers = useSelector(selectCompanyMembers);
  const parsedCommentsState = useSelector(selectParsedComments);
  const navigate = useNamedNavigate();

  useEffect(() => {
    if (isOpen && query?.company_id) {
      dispatch(loadCompanyMembers(query?.company_id));
    }
  }, [isOpen, query?.company_id, dispatch]);

  useEffect(() => {
    if (query?.comments && companyMembers.length > 0) {
      query.comments.forEach((comment) => {
        if (!parsedCommentsState[comment.id]) {
          dispatch(
            parseComment(comment.id, comment.comment, query?.company_id)
          );
        }
      });
    }
  }, [
    query?.comments,
    companyMembers,
    dispatch,
    parsedCommentsState,
    query?.company_id
  ]);

  // Fetch linked document if present
  const linkedDocumentId = query?.linked_document_id;
  const documentPathParams = useMemo(
    () => (query?.company_id ? { companyId: query.company_id } : undefined),
    [query?.company_id]
  );

  const { itemData: rawDocument } = useEntityRetrieve(
    DataType.Transaction,
    linkedDocumentId || "",
    undefined,
    documentPathParams
  );
  const document = linkedDocumentId ? (rawDocument as Transaction) : undefined;

  const linkedBankTransaction = query?.linked_bank_transaction;

  const handleSend = () => {
    if (reply.trim()) {
      postComment(reply);
      setReply("");
    }
  };

  React.useEffect(() => {
    if (postError) {
      toast({
        status: "error",
        title: "Failed to post reply",
        description: postError.message
      });
    }
  }, [postError, toast]);

  const handleModalClose = useCallback(() => {
    setShowPreview(false);
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      size={showPreview ? "4xl" : "xl"}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <HStack align="stretch" spacing={0}>
          {showPreview && (
            <Stack
              align="flex-start"
              justify="stretch"
              pt={5}
              px={5}
              pb={4}
              flexShrink={0}
              w={430}
            >
              <Button
                variant="link"
                size="sm"
                fontWeight={600}
                color="purple.600"
                rightIcon={<LuMoveRight />}
                onClick={() => setShowPreview(!showPreview)}
              >
                Close Preview
              </Button>

              <Box w="full" h="full">
                {document?.image_url && (
                  <PreviewFile
                    imageUrl={document.image_url}
                    imageDownloadUrl={document?.image_download_url}
                  />
                )}
              </Box>
            </Stack>
          )}
          <Box w="full">
            {query && (
              <ModalHeader pb={0}>
                <Text fontSize={18} fontWeight={700} color="gray.900" mb={1}>
                  {getQueryTitle(query)}
                </Text>
              </ModalHeader>
            )}
            <ModalBody padding={0}>
              <ModalCloseButton />
              {isLoading && (
                <HStack justify="center" align="center" minH={200}>
                  <Spinner />
                </HStack>
              )}
              {error && (
                <Box color="red.500" py={8} textAlign="center">
                  <Text fontWeight={600}>Failed to load query details</Text>
                  <Text fontSize={14}>{error.message}</Text>
                </Box>
              )}
              {!query && !isLoading && (
                <Box py={8} textAlign="center">
                  <Text fontWeight={600}>No details found for this query.</Text>
                </Box>
              )}
              {query && (
                <VStack align="stretch" spacing={6}>
                  <Code bg="blackAlpha.100">
                    <Box paddingLeft={6} paddingRight={12} paddingBottom={8}>
                      <Box borderBottom="1px solid #E5EAF1" mb={3} />
                      <HStack
                        color={
                          query.status === "OPEN" ? "purple.600" : "red.500"
                        }
                      >
                        <Icon fontSize={24} as={PiChatsCircle} />
                        <Text
                          fontWeight={700}
                          fontSize={16}
                          mb={0.5}
                          lineHeight={1.2}
                        >
                          {query.title}
                        </Text>
                      </HStack>

                      <Box pl={8}>
                        <Text
                          fontSize={12}
                          fontWeight={500}
                          color="gray.500"
                          mb={2}
                        >
                          Received on:{" "}
                          {format(
                            new Date(query.created_at),
                            "d MMM yyyy, h:mm a"
                          )}
                        </Text>
                        <Box borderBottom="1px solid #dce0e6" mt={2} mb={2} />
                        <Text
                          as="pre"
                          fontWeight={600}
                          fontSize={13}
                          mb={2}
                          color="gray.800"
                        >
                          Query Details
                        </Text>
                        <SimpleGrid
                          columns={2}
                          spacingY={1}
                          fontFamily="mono"
                          fontSize={13}
                          color="gray.700"
                          mb={2}
                        >
                          <Text as="pre" fontWeight={500}>
                            <b>Date:</b>{" "}
                            <span
                              style={{
                                color:
                                  document?.extracted_data?.date_of_invoice ||
                                  linkedBankTransaction?.date
                                    ? undefined
                                    : "#B0B0B0"
                              }}
                            >
                              {document?.extracted_data?.date_of_invoice ||
                              linkedBankTransaction?.date
                                ? format(
                                    new Date(
                                      document?.extracted_data
                                        ?.date_of_invoice! ||
                                        linkedBankTransaction?.date!
                                    ),
                                    "d MMMM yyyy"
                                  )
                                : "-"}
                            </span>
                          </Text>
                          <Text as="pre" fontWeight={500} textAlign="right">
                            <b>Amount:</b>{" "}
                            <span
                              style={{
                                color:
                                  document?.extracted_data?.total ||
                                  linkedBankTransaction?.amount
                                    ? undefined
                                    : "#B0B0B0"
                              }}
                            >
                              {document?.extracted_data?.total
                                ? `£ ${document.extracted_data.total}`
                                : linkedBankTransaction?.amount || "-"}
                            </span>
                          </Text>
                          <Text as="pre" fontWeight={500}>
                            <b>Supplier:</b>{" "}
                            <span
                              style={{
                                color:
                                  document?.extracted_data?.supplier_name ||
                                  linkedBankTransaction?.supplier
                                    ? undefined
                                    : "#B0B0B0",
                                fontWeight: 300,
                                textWrap: "wrap"
                              }}
                            >
                              {document?.extracted_data?.supplier_name ||
                                linkedBankTransaction?.supplier ||
                                "-"}
                            </span>
                          </Text>
                          {!linkedBankTransaction && (
                            <Text as="pre" fontWeight={500} textAlign="right">
                              <b>VAT:</b>{" "}
                              <span
                                style={{
                                  color:
                                    !document?.extracted_data?.vat ||
                                    document.extracted_data.vat === 0
                                      ? "#B0B0B0"
                                      : undefined
                                }}
                              >
                                {document?.extracted_data?.vat === 0 &&
                                  "Missing"}
                                {document?.extracted_data?.vat &&
                                document?.extracted_data?.vat !== 0
                                  ? `£ ${document.extracted_data.vat}`
                                  : "-"}
                              </span>
                            </Text>
                          )}
                          {document && (
                            <Text
                              as="pre"
                              fontWeight={500}
                              gridColumn="1 / span 2"
                            >
                              <b>Invoice:</b>&nbsp;
                              <Box as="span" color="primary.500">
                                <Button
                                  variant="link"
                                  size="xs"
                                  colorScheme="primary"
                                  onClick={() =>
                                    navigate(
                                      getCrudRouteName(
                                        DataType.Transaction,
                                        CrudRouteType.Edit
                                      ),
                                      {
                                        clientId: query?.company_id,
                                        transactionId: document.id
                                      }
                                    )
                                  }
                                >
                                  {document.extracted_data.invoice_id || "-"}
                                </Button>
                                &nbsp;
                                <Button
                                  variant="link"
                                  size="xs"
                                  colorScheme="primary"
                                  onClick={() => setShowPreview(true)}
                                >
                                  (Preview)
                                </Button>
                              </Box>
                            </Text>
                          )}
                          {query.assignee && (
                            <Text
                              as="pre"
                              fontWeight={500}
                              gridColumn="1 / span 2"
                            >
                              <b>Assignee:</b>&nbsp;
                              <span>
                                {query.assignee?.first_name}&nbsp;
                                {query.assignee?.last_name}
                              </span>
                            </Text>
                          )}
                          {query.creator && (
                            <Text
                              as="pre"
                              fontWeight={500}
                              gridColumn="1 / span 2"
                            >
                              <b>Query Creator:</b>&nbsp;
                              <span>
                                {query.creator?.first_name}&nbsp;
                                {query.creator?.last_name}
                              </span>
                            </Text>
                          )}
                          <Text
                            as="pre"
                            fontWeight={500}
                            gridColumn="1 / span 2"
                          >
                            <b>Message:</b>&nbsp;
                            <span
                              style={{
                                color: query.description ? "#888" : "#B0B0B0",
                                fontWeight: 300,
                                whiteSpace: "break-spaces"
                              }}
                            >
                              {query.description || "None"}
                            </span>
                          </Text>
                        </SimpleGrid>
                      </Box>
                    </Box>
                  </Code>
                  <Box paddingLeft={12} paddingRight={12}>
                    <Text
                      fontWeight={600}
                      mb={2}
                      color="gray.800"
                      fontSize={14}
                    >
                      Previous Replies
                    </Text>
                    <Box
                      maxH={600}
                      overflowY="auto"
                      borderRadius={8}
                      border="1px solid #E5EAF1"
                      p={3}
                      bg="#F8FAFC"
                    >
                      {query.comments.length === 0 ? (
                        <Text fontSize={14} color="gray.500">
                          No replies yet.
                        </Text>
                      ) : (
                        <VStack align="stretch" spacing={4}>
                          {query.comments.map((comment) => {
                            const isCurrentUser =
                              comment.commenter?.id &&
                              currentUser?.id &&
                              comment.commenter.id === currentUser.id;
                            const parsedComment =
                              parsedCommentsState[comment.id];

                            return (
                              <HStack
                                key={comment.id}
                                justify={
                                  isCurrentUser ? "flex-end" : "flex-start"
                                }
                                width="100%"
                                align="stretch"
                              >
                                {!isCurrentUser && (
                                  <Box
                                    width="4px"
                                    borderRadius="4px"
                                    bg="gray.200"
                                    mr={2}
                                    alignSelf="stretch"
                                  />
                                )}
                                <Box
                                  px={2}
                                  py={1}
                                  borderRadius={6}
                                  bg="white"
                                  maxW="70%"
                                  textAlign={isCurrentUser ? "right" : "left"}
                                  boxShadow="sm"
                                  width="auto"
                                >
                                  <Text
                                    fontWeight={700}
                                    fontSize={15}
                                    color="gray.800"
                                    mb={0.5}
                                  >
                                    {comment.commenter?.first_name || "User"}
                                    {comment.commenter?.last_name
                                      ? ` ${comment.commenter.last_name}`
                                      : ""}
                                  </Text>
                                  <Box mb={1}>
                                    <ParsedCommentRenderer
                                      parsedComment={
                                        parsedComment || {
                                          rawText: comment.comment,
                                          segments: [
                                            {
                                              type: "text",
                                              content: comment.comment
                                            }
                                          ],
                                          mentions: []
                                        }
                                      }
                                      companyMembers={companyMembers}
                                      size="md"
                                    />
                                  </Box>
                                  <Text fontSize={12} color="gray.500">
                                    {format(
                                      new Date(comment.created_at),
                                      "d MMM yyyy, h:mm a"
                                    )}
                                  </Text>
                                </Box>
                                {isCurrentUser && (
                                  <Box
                                    width="4px"
                                    borderRadius="4px"
                                    bg="purple.600"
                                    ml={2}
                                    alignSelf="stretch"
                                  />
                                )}
                              </HStack>
                            );
                          })}
                        </VStack>
                      )}
                    </Box>
                  </Box>
                  {query?.status !== "RESOLVED" && (
                    <Box paddingLeft={12} paddingRight={12}>
                      <Text
                        fontWeight={600}
                        mb={1}
                        color="gray.800"
                        fontSize={14}
                      >
                        Reply
                      </Text>
                      <CommentInput
                        value={reply}
                        onChange={setReply}
                        onSend={handleSend}
                        placeholder="Type your reply..."
                        companyMembers={companyMembers}
                        isDisabled={isPosting}
                      />
                    </Box>
                  )}
                  <Divider />
                </VStack>
              )}
            </ModalBody>
            <ModalFooter
              paddingLeft={12}
              paddingRight={12}
              justifyContent="space-between"
            >
              <Button variant="outline" onClick={onClose} mr={3}>
                Cancel
              </Button>
              {query?.status !== "RESOLVED" && query?.status !== "CLOSED" && (
                <Button
                  colorScheme="red"
                  rightIcon={<FiSend />}
                  onClick={handleSend}
                  isLoading={isPosting}
                  isDisabled={!reply.trim() || isPosting}
                >
                  Send
                </Button>
              )}

              {(query?.status === "RESOLVED" || query?.status === "CLOSED") && (
                <Center
                  bg="green.50"
                  color="green.600"
                  p={2}
                  rounded={6}
                  gap={2}
                >
                  <FiCheckCircle />
                  <Text fontSize="sm" fontWeight="medium">
                    This query has been resolved
                  </Text>
                </Center>
              )}
            </ModalFooter>
          </Box>
        </HStack>
      </ModalContent>
    </Modal>
  );
};
